/**
 * EditActivityById.
 *
 * Thin wrapper around `EditActivityModal` for surfaces that only have an
 * activity ID at click-time (e.g. the Data Quality grid). Fetches the
 * full `ActivityRecord` lazily so the modal can render the same fields
 * the wizard uses, with `scope` and `activityType` locked.
 *
 * Mirrors the gap-cell flow: clicking a non-gap cell with a single
 * contributor opens this modal directly; multi-contributor buckets pass
 * through the picker first, then mount this with the chosen id.
 */

import { Loader2 } from 'lucide-react';
import { Modal, ModalBody } from 'reactstrap';
import { useGetActivityQuery } from '../../../api/emissions';
import { EditActivityModal } from '../../EditActivityModal';

interface EditActivityByIdProps {
  /** When non-null, opens the modal for this activity. */
  activityId: string | null;
  onClose: () => void;
  /** Asset's initiative id — passed through to the API hook. */
  initiativeId: string;
}

export const EditActivityById = ({ activityId, onClose, initiativeId }: EditActivityByIdProps) => {
  const { data: activity, isLoading } = useGetActivityQuery(
    { initiativeId, activityId: activityId ?? '' },
    { skip: !activityId },
  );

  // While the activity is loading, show a tiny loading shell so the user
  // sees an immediate response after clicking the cell. As soon as the
  // record arrives we hand off to `EditActivityModal`, which seeds its
  // own state from the activity.
  if (activityId && (isLoading || !activity)) {
    return (
      <Modal isOpen={true} centered size='md' toggle={onClose}>
        <ModalBody>
          <div className='tw:flex tw:items-center tw:justify-center tw:gap-2 tw:py-6 tw:text-muted-foreground'>
            <Loader2 className='tw:size-4 tw:animate-spin' />
            Loading activity…
          </div>
        </ModalBody>
      </Modal>
    );
  }

  if (!activity) return null;

  return (
    <EditActivityModal
      isOpen={activityId !== null}
      onClose={onClose}
      initiativeId={initiativeId}
      activity={activity}
    />
  );
};
